import React from 'react';
import { ArrowLeft, AlertTriangle, Target, Heart, Coffee } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { useAssessmentStore } from '../../store';

interface CissResultsProps {
  scores: {
    task: {
      score: number;
      percentage: number;
      interpretation: string;
      color: string;
    };
    emotion: {
      score: number;
      percentage: number;
      interpretation: string;
      color: string;
    };
    avoidance: {
      score: number;
      percentage: number;
      interpretation: string;
      color: string;
    };
    primaryStyle: 'task' | 'emotion' | 'avoidance';
  };
  onBack: () => void;
}

const CissResults: React.FC<CissResultsProps> = ({ scores, onBack }) => {
  const { setCissResults, setAssessmentComplete } = useAssessmentStore();

  React.useEffect(() => {
    setCissResults(scores);
    setAssessmentComplete('ciss');
  }, [scores]);

  const data = [
    {
      name: 'Task-oriented',
      value: scores.task.percentage,
      color: '#4f46e5',
      score: scores.task.score
    },
    {
      name: 'Emotion-oriented',
      value: scores.emotion.percentage,
      color: '#ef4444',
      score: scores.emotion.score
    },
    {
      name: 'Avoidance-oriented',
      value: scores.avoidance.percentage,
      color: '#f59e0b',
      score: scores.avoidance.score
    }
  ];

  const getStyleDescription = () => {
    switch (scores.primaryStyle) {
      case 'task':
        return "You tend to use a task-oriented coping style, which means you actively seek solutions when facing stress. This is an adaptive coping strategy that helps effectively solve problems and reduce stress.";
      case 'emotion':
        return "You tend to use an emotion-oriented coping style, which means you focus more on emotional responses when facing stress. While it's important to acknowledge emotions, consider balancing this with problem-solving approaches.";
      case 'avoidance':
        return "You tend to use an avoidance-oriented coping style, which means you may temporarily avoid or distract yourself from stressors. While this can help in the short term, consider developing more active problem-solving strategies for long-term stress management.";
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Coping Style Assessment Results</h2>
        </div>

        <div className="space-y-6">
          {/* Main Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Coping Style Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Bar dataKey="value" name="Percentage">
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detailed Scores */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Target className="w-5 h-5 text-indigo-500 mr-2" />
                <h4 className="font-medium text-indigo-900">Task-oriented</h4>
              </div>
              <div className="text-2xl font-bold text-indigo-700 mb-2">
                {scores.task.score}
              </div>
              <p className="text-sm text-indigo-600">
                {scores.task.interpretation}
              </p>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Heart className="w-5 h-5 text-red-500 mr-2" />
                <h4 className="font-medium text-red-900">Emotion-oriented</h4>
              </div>
              <div className="text-2xl font-bold text-red-700 mb-2">
                {scores.emotion.score}
              </div>
              <p className="text-sm text-red-600">
                {scores.emotion.interpretation}
              </p>
            </div>

            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Coffee className="w-5 h-5 text-amber-500 mr-2" />
                <h4 className="font-medium text-amber-900">Avoidance-oriented</h4>
              </div>
              <div className="text-2xl font-bold text-amber-700 mb-2">
                {scores.avoidance.score}
              </div>
              <p className="text-sm text-amber-600">
                {scores.avoidance.interpretation}
              </p>
            </div>
          </div>

          {/* Primary Style Analysis */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Primary Coping Style Analysis</h3>
            <p className="text-gray-600 mb-4">{getStyleDescription()}</p>
          </div>

          {/* Recommendations */}
          <div className="bg-indigo-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-indigo-900 mb-4">Recommendations</h3>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-indigo-800 mb-2">Enhance Task-oriented Skills</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• Learn problem-solving techniques</li>
                  <li>• Develop planning and organization skills</li>
                  <li>• Set specific, achievable goals</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-indigo-800 mb-2">Emotional Management</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• Practice mindfulness meditation</li>
                  <li>• Learn to identify and express emotions</li>
                  <li>• Develop healthy stress relief methods</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-indigo-800 mb-2">Balance Coping Strategies</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• Use different coping styles as appropriate</li>
                  <li>• Build a support system</li>
                  <li>• Maintain healthy lifestyle habits</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium mb-1">Important Notice</p>
                <p>This assessment tool is for reference only and should not be used for clinical diagnosis. If you are experiencing emotional distress, please seek help from a qualified mental health professional or physician.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CissResults;